//
// Author(s):
// Fr. Matthew Spencer, OSJ <mspencer@osjusa.org>
//
// Copyright (c) 2008-2016 Fr. Matthew Spencer, OSJ
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.
//

export interface GlyphPath {
  /** a negative path is filled with ChantContext.negativeFillColor, e.g. the hole of a punctum cavum */
  type: "positive" | "negative";
  /** svg path data */
  data: string;
  /** the path, parsed for drawing on a canvas (set the first time it's drawn) */
  path2d?: Path2D;
}

export interface Glyph {
  paths: GlyphPath[];
  bounds: { x: number; y: number; width: number; height: number };
  origin: { x: number; y: number };
  align?: "left" | "right";
}

// generated based on the svg data
export const Glyphs: { [name: string]: Glyph } = {
  None: {
    paths: [
      {
        type: "positive",
        data: ""
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 0,
      height: 0
    },
    origin: {
      x: 0,
      y: 0
    },
    align: "left"
  },
  // a linea's note (gabc =), which draws nothing: its LineaVisualizer draws the linea
  Linea: {
    paths: [
      {
        type: "positive",
        data: ""
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 0,
      height: 0
    },
    origin: {
      x: 0,
      y: 0
    },
    align: "left"
  },
  AcuteAccent: {
    paths: [
      {
        type: "positive",
        data:
          "M4 0C-.614.52-.614.52-.803-3.182l60.768-108.422c4.52-7.182 10.543-13.67 18.075-13.67 5.27 0 14.31 1.264 23.346 7.793 7.53 5.223 8.803 11.752 8.803 16.975 0 3.917-.52 11.1-8.05 17.628L4 0z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 0, // TODO: figure out a better way to align this horizontally.  width should be 110.992,
      height: 125.794
    },
    origin: {
      x: 0.803,
      y: 125.274
    },
    align: "left"
  },
  GraveAccent: {
    paths: [
      {
        type: "positive",
        data:
          "M105.386.26C110 .78 110 .78 110.189-2.922l-60.768-108.422c-4.52-7.182-10.543-13.67-18.075-13.67-5.27 0-14.31 1.264-23.346 7.793-7.53 5.223-8.803 11.752-8.803 16.975 0 3.917.52 11.1 8.05 17.628L105.386.26z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 0, //110.992,
      height: 125.794
    },
    origin: {
      x: 0, // -110.992,
      y: 125.274
    },
    align: "left"
  },
  Circle: {
    paths: [
      {
        type: "positive",
        data:
          "M0 -50A50 50 0 0 0 100 -50 50 50 0 0 0 0 -50M10 -50A40 40 0 0 1 90 -50 40 40 0 0 1 10 -50"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 100
    },
    origin: {
      x: -50,
      y: 100
    }
  },
  Semicircle: {
    paths: [
      {
        type: "positive",
        data:
          "M0 -50A50 50 0 0 0 100 -50 5 5 0 0 0 90 -50 40 40 0 0 1 10 -50 5 5 0 0 0 0 -50"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 55
    },
    origin: {
      x: -50,
      y: 50
    }
  },
  ReversedSemicircle: {
    paths: [
      {
        type: "positive",
        data:
          "M0 0A50 50 0 0 1 100 0 5 5 0 0 1 90 0 40 40 0 0 0 10 0 5 5 0 0 1 0 0"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 55
    },
    origin: {
      x: -50,
      y: 50
    }
  },
  Stropha: {
    paths: [
      {
        type: "positive",
        data:
          "M1.22-73.438c4.165 13.02 12.238 27.084 24.217 42.188L49.657 0 34.812 27.344C18.666 55.47-.084 72.396-21.438 78.124c4.687-3.645 7.03-8.593 7.03-14.843 0-8.853-4.947-20.572-14.843-35.155L-48 0 1.22-73.438z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 97.657,
      height: 151.562
    },
    origin: {
      x: 48,
      y: 73.438
    },
    align: "left"
  },
  // the stropha aucta: Stropha, with its tail (the half below its side
  // corners) a quarter longer
  StrophaLiquescent: {
    paths: [
      {
        type: "positive",
        data:
          "M1.22-73.438c4.165 13.02 12.238 27.084 24.217 42.188L49.657 0 34.812 34.18C18.666 69.338-.084 90.495-21.438 97.655c4.687-4.556 7.03-10.741 7.03-18.554 0-11.066-4.947-25.715-14.843-43.944L-48 0 1.22-73.438z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 97.657,
      height: 171.093
    },
    origin: {
      x: 48,
      y: 73.438
    },
    align: "left"
  },
  BeginningAscLiquescent: {
    paths: [
      {
        type: "positive",
        data:
          "M-50 43.688V-61c4.167 7.292 12.76 10.938 25.78 10.938 9.376 0 20.053-1.563 32.032-4.688C31.773-60.48 45.833-71.677 50-88.344v117.97C43.75 42.645 32.812 51.5 17.187 56.186-.52 61.398-15.886 64-28.906 64-42.97 64-50 57.23-50 43.687z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 152.344
    },
    origin: {
      x: 50,
      y: 88.344
    },
    align: "left"
  },
  BeginningDesLiquescent: {
    paths: [
      {
        type: "positive",
        data:
          "M-50-56.03c0-13.022 7.03-19.532 21.094-19.532 13.02 0 28.385 2.604 46.093 7.812C32.813-63.583 43.75-54.73 50-41.187V76C45.833 59.854 31.77 48.656 7.812 42.406c-11.98-3.125-22.656-4.687-32.03-4.687-13.022 0-21.615 3.905-25.782 11.718v-105.47z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 151.562
    },
    origin: {
      x: 50,
      y: 75.562
    },
    align: "left"
  },
  CustosDescLong: {
    paths: [
      {
        type: "positive",
        data:
          "M39.063 273.472c5.73.52 7.29-6.25 4.687-20.312V-65.59c-13.542 2.083-24.22 5.468-32.03 10.156C3.905-50.226 0-43.714 0-35.904V71.91c5.73-5.21 10.677-8.594 14.844-10.157 5.73-1.562 12.24-2.343 19.53-2.343v196.875c0 11.458 1.563 17.187 4.688 17.187"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 46.353,
      height: 339.582
    },
    origin: {
      x: 0,
      y: 65.59
    },
    align: "left"
  },
  CustosDescShort: {
    paths: [
      {
        type: "positive",
        data:
          "M34.375 191.923c0 8.333 1.563 12.24 4.688 11.72 3.125-.522 4.687-7.033 4.687-19.533v-250c-13.542 2.084-24.22 5.47-32.03 10.157C3.905-50.525 0-44.015 0-36.203V71.61c5.73-5.208 10.677-8.593 14.844-10.156 5.73-1.562 12.24-2.344 19.53-2.344v132.813z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 43.75,
      height: 270.053
    },
    origin: {
      x: 0,
      y: 65.89
    },
    align: "left"
  },
  CustosLong: {
    paths: [
      {
        type: "positive",
        data:
          "M39.063-269.562c5.73-.52 7.29 6.25 4.687 20.312V69.5c-13.542-2.083-24.22-5.47-32.03-10.156C3.905 54.134 0 47.624 0 39.812V-68c5.73 5.208 10.677 8.594 14.844 10.156 5.73 1.563 12.24 2.344 19.53 2.344v-196.875c0-11.458 1.563-17.187 4.688-17.187z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 46.353,
      height: 339.582
    },
    origin: {
      x: 0,
      y: 270.082
    },
    align: "left"
  },
  CustosShort: {
    paths: [
      {
        type: "positive",
        data:
          "M34.375-188.125c0-8.333 1.563-12.24 4.688-11.72 3.125.522 4.687 7.033 4.687 19.532v250c-13.542-2.083-24.22-5.468-32.03-10.156C3.905 54.324 0 47.813 0 40V-67.813c5.73 5.21 10.677 8.594 14.844 10.157 5.73 1.562 12.24 2.344 19.53 2.343v-132.812z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 43.75,
      height: 270.052
    },
    origin: {
      x: 0,
      y: 200.365
    },
    align: "left"
  },
  DoClef: {
    paths: [
      {
        type: "positive",
        data:
          "M0 98.406V-97.688C0-118 5.99-134.275 17.97-146.516c11.978-12.24 27.603-18.36 46.874-18.36 10.937 0 19.53 3.126 25.78 9.376s9.376 14.583 9.376 25v107.813l-6.25-5.47c-4.167-3.645-10.287-7.42-18.36-11.327-8.072-3.907-16.796-5.86-26.17-5.86-11.46 0-21.486 4.427-30.08 13.282-8.593 8.854-12.89 19.53-12.89 32.03s4.297 23.308 12.89 32.423c8.594 9.115 18.62 13.672 30.08 13.672 9.374 0 18.098-1.822 26.17-5.468 8.073-3.646 14.193-7.292 18.36-10.938l6.25-6.25V132c0 9.896-3.125 18.1-9.375 24.61-6.25 6.51-14.844 9.765-25.78 9.765-19.272 0-34.897-6.25-46.876-18.75C5.99 135.125 0 118.72 0 98.405z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 331.251
    },
    origin: {
      x: 0,
      y: 164.876
    },
    align: "left"
  },
  FaClef: {
    paths: [
      {
        type: "positive",
        data:
          "M85.156-32v193.75c0 9.375-1.562 14.323-4.687 14.844-1.564 0-2.605-.52-3.126-1.563-.52-1.04-.782-2.603-.78-4.686V56.28c-8.335-8.332-19.793-12.5-34.376-12.5-17.71 0-31.77 3.907-42.188 11.72V-32c0-18.23 14.193-27.344 42.578-27.344 28.385 0 42.578 9.115 42.578 27.344zM98.438 93V-92.156c0-19.27 5.73-34.896 17.187-46.875 11.458-11.98 26.562-17.97 45.313-17.97 10.937 0 19.14 2.865 24.61 8.594 5.467 5.73 8.202 13.542 8.202 23.437v103.126l-5.47-4.687c-3.645-3.647-9.374-7.293-17.186-10.94-7.813-3.645-15.886-5.467-24.22-5.468-11.978 0-22.004 4.167-30.077 12.5-8.073 8.334-12.11 18.36-12.11 30.08 0 11.717 4.037 22.004 12.11 30.858s18.1 13.28 30.078 13.28c8.333 0 16.406-1.822 24.22-5.468 7.81-3.645 13.54-7.03 17.186-10.156l5.47-5.468V125.81c0 9.896-2.865 17.84-8.594 23.83-5.73 5.988-13.802 8.983-24.22 8.983-18.75 0-33.853-6.12-45.31-18.36-11.46-12.24-17.19-27.994-17.19-47.265z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 193.752,
      height: 333.595
    },
    origin: {
      x: 0.001,
      y: 157.001
    },
    align: "left"
  },
  TrebleClef: {
    paths: [
      {
        type: "positive",
        data:
          "M291 202C95 202 0 70 0-70c0-161 122-276 238-371 9-8 8-9 7-19-6-33-11-85-11-131 0-83 19-183 78-249 16-18 41-38 52-38 9 0 30 22 42 40 32 48 52 116 52 186 0 122-66 224-152 305-5 5-7 5-5 15l20 116c2 14 2 14 23 14 118 0 193 91 193 193 0 91-54 159-135 191-11 4-12 4-10 13 9 50 23 126 23 171 0 136-104 160-157 160-121 0-152-78-152-130 0-50 32-92 85-92 49 0 77 38 77 82 0 46-29 68-54 75-18 5-25 8-25 13 0 11 21 24 64 24 47 0 127-15 127-133 0-38-12-107-22-161-1-10-3-9-12-7-16 3-36 5-55 5ZM64-16c0 111 90 189 230 189 16 0 32-2 45-4 12-3 13-3 11-11L310-81c-1-9-3-9-16-6-48 13-80 48-80 93 0 37 24 72 58 86 6 2 12 5 12 10s-3 9-10 9c-5 0-15-2-21-5-55-18-92-69-92-141 0-68 45-131 116-155 14-5 14-4 12-20l-17-102c-2-9-3-10-11-3-38 30-75 60-123 113C72-126 64-63 64-16Zm312-738c-49 0-106 76-106 201 0 26 1 53 4 74 2 10 5 11 11 6 63-55 139-127 139-216 0-44-22-65-48-65ZM353-94c-10 0-11 2-9 12l39 228c2 9 3 9 13 4 45-22 75-63 75-113 0-71-53-125-118-131Z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 671 * 0.8,
      height: 1756 * 0.8
    },
    origin: {
      x: 0,
      y: 1098 * 0.8
    },
    align: "left"
  },
  TrebleClefSmall: {
    paths: [
      {
        type: "positive",
        data:
          "M218 151C71 151 0 52 0-52c0-121 92-207 178-279 7-6 7-7 6-14-5-25-9-64-9-98 0-63 15-138 59-187 12-13 31-29 39-29 7 0 22 17 31 30 25 36 39 88 39 140 0 92-49 168-114 229-3 3-5 4-3 11l15 87c1 11 1 11 17 11 88 0 145 68 145 144 0 68-41 119-101 143-9 4-9 3-8 10 7 38 17 95 17 129 0 102-78 120-118 120-90 0-114-59-114-98 0-37 24-69 64-69 37 0 58 28 58 61 0 35-22 51-40 57-14 4-19 6-19 10 0 8 15 17 48 17 35 0 95-11 95-99 0-29-9-81-16-121-1-7-3-7-9-5-12 2-28 3-42 3ZM48-12c0 83 68 142 173 142 12 0 24-2 33-3 9-2 10-2 9-9L233-61c-1-6-3-7-12-4-37 10-60 36-60 70 0 27 18 53 43 64 4 2 9 4 9 8 0 3-2 6-7 6-4 0-12-1-16-3-41-14-69-52-69-106 0-51 34-99 87-117 10-3 10-3 9-14l-13-77c-1-7-2-7-8-2-29 22-56 45-92 85C54-95 48-47 48-12Zm234-554c-37 0-80 58-80 151 0 20 1 40 4 56 1 7 3 8 8 4 47-41 104-95 104-162 0-33-16-49-36-49ZM265-70c-8-1-8 1-7 8l29 171c2 7 3 7 10 4 34-17 56-48 56-85 0-53-40-94-88-98Z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 671 * 0.6,
      height: 1756 * 0.6
    },
    origin: {
      x: 0,
      y: 1098 * 0.6
    },
    align: "left"
  },
  ChiRhoClef: {
    paths: [
      {
        type: "positive",
        data: "M105.917 147.774c-2.067-3.344.33-5.742 7.093-7.094 9.764-1.953 10.878-8.732 10.878-66.221V23.014l-31.89 18.927c-17.54 10.41-32.33 18.927-32.866 18.927s-6.285 3.525-12.774 7.833c-10.211 6.78-11.65 8.56-10.708 13.24 2.437 12.105 2.353 13.736-.74 14.383-2.058.43-5.723-4.048-10.336-12.629-3.93-7.312-10.92-19.208-15.533-26.436-7.792-12.212-8.838-17.574-3.428-17.574 1.22 0 4.149 2.455 6.509 5.456 3.206 4.075 5.547 5.14 9.257 4.209 4.153-1.043 57.07-30.529 80.73-44.985L108.93.2l-39.828-22.673c-21.905-12.47-42.917-23.84-46.694-25.265-6.542-2.47-7.139-2.258-12.641 4.484-5.147 6.307-6.079 6.71-8.57 3.708-2.384-2.873-1.55-5.313 5.673-16.614 4.657-7.285 11.576-19.204 15.376-26.486 6.447-12.353 10.761-15.93 13.624-11.299.66 1.068.266 4.396-.875 7.396-1.14 3-1.54 6.847-.887 8.549 1.381 3.6 20.012 15.393 60.125 38.062l28.597 16.16.556-77.692c.613-85.706.355-87.62-11.832-87.62-6.286 0-10.966-3.346-10.966-7.838 0-4.44 92.037-3.911 102.811.59 21.627 9.037 33.743 26.842 33.76 49.614.03 37.863-30.53 65.667-72.177 65.667H151.654l-.644 28.499-.644 28.499 5.296-3.53c2.912-1.943 16.734-10.076 30.715-18.075C219.121-64.4 233.238-73.793 234.811-77.894c.694-1.81-.277-7.137-2.158-11.838-2.642-6.603-2.847-9.12-.902-11.059 3.327-3.316 6.53 1.033 24.387 33.12 9.75 17.517 13.444 26.059 11.928 27.574-1.515 1.516-3.723.761-7.126-2.436-6.943-6.522-11.612-5.87-31.58 4.41C207.227-26.73 166.254-2.377 166.254-.616c0 1.199 13.66 9.394 43.425 26.054 17.366 9.72 39.944 20.601 42.746 20.601 1.44 0 5.28-3.336 8.533-7.414 5.798-7.267 11.8-9.485 14.378-5.313.714 1.155-1.527 5.683-4.98 10.061-3.452 4.38-11.1 16.541-16.996 27.027-5.896 10.485-11.563 20.488-12.594 22.227-1.564 2.64-2.43 2.7-5.24.369-2.669-2.215-2.895-3.831-1.09-7.794 1.254-2.75 1.706-6.493 1.005-8.319-.7-1.825-9.166-8.384-18.813-14.576-17.64-11.321-62.464-37.45-64.246-37.45-2.144 0-.858 101.326 1.367 107.709 1.688 4.841 4.15 7.137 9.003 8.394 3.674.952 6.68 3.1 6.68 4.774 0 2.48-5.75 3.151-31.086 3.628-19.604.37-31.582-.218-32.43-1.589M183.434-94.909c10.38-3.663 19.694-11.631 24.662-21.101 5.14-9.799 4.065-30.404-2.313-44.336-7.44-16.251-18.35-23.003-39-24.132l-15.357-.84v93.668h11.384c6.262 0 15.542-1.466 20.624-3.26"
      }
    ],
    bounds: {
      x: 0,
      y: -200,
      width: 275.469,
      height: 149.471
    },
    origin: {
      x: 0,
      y: 0
    },
    align: "left"
  },
  ChiRhoClefSans: {
    paths: [
      {
        type: "positive",
        data: "M123.387 156.19c-.792-.792-1.44-29.618-1.44-64.058 0-48.772-.562-62.604-2.539-62.557-1.396.033-23.553 18.311-49.237 40.617s-47.76 40.556-49.06 40.556c-3.278 0-21.322-20.403-20.424-23.095.402-1.206 10.304-9.069 22.004-17.473C50.956 49.878 111.43 3.313 113.154.522c1.612-2.607-3.36-7.148-57.717-52.722-24.908-20.883-41.125-35.892-41.125-38.062 0-1.97 3.585-7.584 7.966-12.475 7.684-8.576 8.147-8.784 13.027-5.846 2.783 1.676 23.067 19.725 45.076 40.11 22.008 20.384 40.821 37.062 41.806 37.062 2.3 0 2.198-57.858-.201-113.137-1.493-34.414-2.546-44.107-5.236-48.212-3.002-4.582-3.027-5.269-.233-6.34 1.724-.662 19.415-.9 39.314-.53 34.178.638 36.68.956 45.228 5.752 23.096 12.959 30.55 46.904 14.709 66.982-8.198 10.39-28.334 19.756-49.143 22.857l-17.262 2.573c-.553 37.059-.335 74.117.609 74.117.943 0 22.966-17.82 48.94-39.601s47.996-39.602 48.939-39.602c1.922 0 22.347 22.392 22.347 24.5 0 .742-10.8 8.61-23.998 17.484C206.435-37.834 156.47-.758 156.47 2.014c0 2.548 64.949 54.22 95.854 76.26 8.6 6.133 15.682 12.423 15.74 13.978.14 3.873-17.812 24.589-21.31 24.589-1.552 0-10.849-8.332-20.66-18.515-21.38-22.192-68.878-65.502-72.838-66.416-2.35-.543-2.909 9.778-3.357 61.94l-.537 62.592-12.268.594c-6.748.326-12.917-.054-13.708-.846m55.249-278.9c17.348-8.85 23.98-25.107 16.832-41.264-5.617-12.7-14.508-18.405-30.24-19.407-12.267-.78-12.818-.608-14.81 4.632-2.218 5.831-2.89 51.452-.838 56.8 1.803 4.697 19.252 4.24 29.056-.76"
      }
    ],
    bounds: {
      x: 0,
      y: -200,
      width: 270,
      height: 157
    },
    origin: {
      x: 0,
      y: 0
    },
    align: "left"
  },
  Flat: {
    paths: [
      {
        type: "positive",
        data:
          "M7.813-204.406c4.166 0 6.25 5.208 6.25 15.625L12.5-10.657C33.854 13.302 54.167 25.28 73.438 25.28c9.374 0 14.062-4.686 14.062-14.06 0-6.25-1.042-11.72-3.125-16.407-2.083-4.688-7.03-9.766-14.844-15.235-7.81-5.47-13.02-8.984-15.624-10.547L27.344-45.81V-80.97c17.187 0 33.073 4.82 47.656 14.454C89.583-56.88 96.875-47.376 96.875-38c0 67.708-.26 101.562-.78 101.563-38.543 0-69.532-12.24-92.97-36.72C0-52.322-1.042-123.936 0-188c0-10.937 2.604-16.406 7.813-16.406z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 97.917,
      height: 267.969
    },
    origin: {
      x: 1.042,
      y: 204.406
    },
    align: "left"
  },
  Mora: {
    paths: [
      {
        type: "positive",
        data:
          "M47.478-24c6.957 0 12.793 2.288 17.49 6.883C69.662-12.52 72-6.904 72-.267c0 6.64-2.337 12.352-7.033 17.118C60.27 21.618 54.435 24 47.477 24c-6.26 0-11.748-2.383-16.444-7.15C26.337 12.086 24 6.374 24-.265c0-6.638 2.337-12.255 7.033-16.85C35.73-21.713 41.217-24 47.478-24z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 48,
      height: 48
    },
    origin: {
      x: -24,
      y: 24
    },
    align: "left"
  },
  Natural: {
    paths: [
      {
        type: "positive",
        data:
          "M7.906-166.563c-2.864 0-5.614.52-8.218 1.563v13.28l.78 56.25.782 78.907v85.157c.52 3.646 2.604 5.73 6.25 6.25l23.438-3.906 23.437-3.907v29.69c0 42.186-.26 63.54-.78 64.06l6.25 2.345c1.04.52 2.082.78 3.124.78 2.603 0 4.947-1.3 7.03-3.905L67.656-71.25c-.52-2.604-2.083-3.906-4.687-3.906-7.814 0-17.19 1.04-28.126 3.125l-19.53 3.124.78-38.28V-165c-2.604-1.042-5.323-1.562-8.188-1.563zM55.938-40v71.875l-41.407 7.03c0-48.436.262-72.655.783-72.655L55.938-40z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 70.311,
      height: 330.469
    },
    origin: {
      x: 0.312,
      y: 166.563
    },
    align: "left"
  },
  Sharp: {
    paths: [
      {
        type: "positive",
        data:
          "m41.725,-73.773c-5.421,-0.241-10.878,5.856-6.549,12.357L67.061,-20.473 61.264,-12.5 13.436,-71.199c-5.634,-5.934-16.988,1.032-11.232,9.783L50.756,0.182 2.203,61.416c-6.745,7.984 3.442,17.859 11.232,9.783L61.264,12.5l5.797,7.973-31.885,40.943c-5.578,6.844 5.588,16.005 11.594,9.783L77.568,33.154 108.367,71.199c4.894,6.717 17.343,-1.575 11.232,-9.783L87.715,20.473 93.873,12.5 141.34,71.199c6.725,7.67 17.509,-2.248 11.596,-9.783L104.02,0.182 152.936,-61.416c5.52,-7.02-5.541,-16.309-11.596,-9.783L93.873,-12.5l-6.158,-7.973 31.884766,-40.943c5.407,-7.045-5.505,-15.924-11.232,-9.783L77.568,-33.154 46.77,-71.199c-1.435,-1.708-3.238,-2.494-5.044922,-2.574zM77.568,-8.516 84.09,0.182 77.568,8.516 70.684,0.182Z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 154.646,
      height: 147.987
    },
    origin: {
      x: 0,
      y: 74.098
    },
    align: "left"
  },
  OriscusAsc: {
    paths: [
      {
        type: "positive",
        data:
          "M50 30.25c0 12.5-3.125 21.354-9.375 26.562-3.125 2.605-7.813 3.907-14.063 3.907-3.125 0-5.99-.522-8.593-1.564-2.605-1.04-5.6-2.474-8.986-4.297C5.6 53.035 2.734 51.603.39 50.56c-2.343-1.04-5.338-2.474-8.984-4.296-3.646-1.823-6.77-3.256-9.375-4.297-2.603-1.043-5.468-1.564-8.593-1.564-6.25 0-10.937 1.563-14.062 4.688C-46.875 50.824-50 59.677-50 71.656v-106.25c0-13.02 3.125-21.875 9.375-26.562 3.125-2.604 7.813-3.906 14.063-3.907 3.125 0 5.99.52 8.593 1.563 2.605 1.042 5.73 2.474 9.376 4.297 3.646 1.823 6.51 2.995 8.594 3.516l10.938 5.468c6.25 3.126 11.458 4.69 15.624 4.69 6.25 0 10.938-1.564 14.063-4.69C46.875-55.426 50-64.02 50-76V30.25z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 147.656
    },
    origin: {
      x: 50,
      y: 76
    },
    align: "left"
  },
  OriscusDes: {
    paths: [
      {
        type: "positive",
        data:
          "M-50 30.844v-106.25c0 11.458 3.125 20.052 9.375 25.78 3.125 3.126 7.813 4.69 14.063 4.688 4.687 0 13.41-3.255 26.17-9.765 12.762-6.51 21.746-9.766 26.954-9.766 6.25 0 10.938 1.303 14.063 3.907C46.875-55.874 50-47.02 50-34V72.25c0-11.98-3.125-20.833-9.375-26.563C37.5 42.563 32.812 41 26.562 41 21.875 41 13.023 44.385 0 51.156c-4.167 2.604-8.594 4.948-13.28 7.032-4.69 2.083-9.116 3.124-13.283 3.124-6.25 0-10.937-1.302-14.062-3.906C-46.875 52.198-50 43.344-50 30.844z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 147.656
    },
    origin: {
      x: 50,
      y: 75.406
    },
    align: "left"
  },
  OriscusLiquescent: {
    paths: [
      {
        type: "positive",
        data:
          "M 19.055,78.887 C 20.242,78.487 21.532,77.890 22.925,77.097 24.318,76.304 26.700882,74.417 30.074,71.438 33.447,68.458 36.524,64.985 39.303,61.019 42.083,57.052 44.563,51.396 46.743,44.05 48.923,36.704 50.013,28.671 50.013,19.950525 L 50.013,-34.226 C 50.013,-54.464 42.074,-64.584 26.195,-64.584 20.248,-64.584 11.519,-61.410 0.007,-55.064 -11.506,-48.717 -20.235,-45.544 -26.182,-45.544 -34.515,-45.544 -40.568,-48.520 -44.340791,-54.473 -48.114,-60.426 -50.000,-67.369 -50.000,-75.303 L -50.000,30.07 C -50.000,49.909 -42.060754,59.829 -26.182,59.829 -21.023,59.829 -12.39,56.455 -0.284,49.709 11.822,42.963 20.648,39.59 26.195,39.59 29.369,40.777 30.362,44.25 29.17479,50.009 27.988,55.768 26.001,62.020829 23.216,68.767 z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 147.656
    },
    origin: {
      x: 50,
      y: 75.406
    },
    align: "left"
  },
  PodatusLower: {
    paths: [
      {
        type: "positive",
        data:
          "M-50 42.17V-50q0-5.42 3.61-5.42h2.41q19.28 3.61 35.55 3.61 4.21 0 10.84-.3t9.04-.3q13.25 0 20.48-4.82t7.23-12.65L50-75.3V41.57q0 3.61-11.14 6.92-11.15 3.32-24.4 3.32-3.01 0-7.83-.3-4.82-.31-12.05-.31-19.88 0-28.31-1.2Q-50 46.99-50 42.17"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 103.6
    },
    origin: {
      x: 50,
      y: 51.8
    },
    align: "left"
  },
  PodatusLowerShort: {
    paths: [
      {
        type: "positive",
        data:
          "M-4.688-30.28c22.396 0 34.636-.262 36.72-.782 5.728-1.563 8.593-5.21 8.593-10.938H50v97.656c0 2.604-1.302 4.167-3.906 4.688-5.21.52-21.355.78-48.438.78-23.958 0-38.54-.26-43.75-.78-2.604 0-3.906-1.302-3.906-3.906v-82.032c0-3.646 1.302-5.468 3.906-5.468h2.344c2.604.52 15.625.78 39.063.78z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 103.124
    },
    origin: {
      x: 50,
      y: 42
    },
    align: "left"
  },
  // the PodatusUpper is really the same as the PodatusUpperShort, just shifted to be fully centered where a normal punctum would be
  PodatusUpper: {
    paths: [
      {
        type: "positive",
        data:
          "m-46.094-55.78c13.542 0 24.61 2.473 33.203 7.42c8.593 4.947 12.891 12.367 12.891 22.264v96.093h-9.375c0-10.938-2.604-19.14-7.812-24.61c-5.21-5.468-14.844-8.203-28.907-8.202c-18.23 0-33.333 4.166-45.312 12.5v-75.782c0-19.79 15.104-29.687 45.312-29.687z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 91.406,
      height: 125.781
    },
    origin: {
      x: 91.406,
      y: 63.781
    },
    align: "right"
  },
  PodatusUpperShort: {
    paths: [
      {
        type: "positive",
        data:
          "M-46.094-63.78c13.542 0 24.61 2.473 33.203 7.42C-4.298-51.41 0-43.99 0-34.093V62h-9.375c0-10.938-2.604-19.14-7.812-24.61-5.21-5.468-14.844-8.203-28.907-8.202-18.23 0-33.333 4.166-45.312 12.5v-75.782c0-19.79 15.104-29.687 45.312-29.687z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 91.406,
      height: 125.781
    },
    origin: {
      x: 91.406,
      y: 63.781
    },
    align: "right"
  },
  PunctumCavum: {
    paths: [
      {
        type: "positive",
        data:
          "M0-60.906c33.333 0 50 9.635 50 28.906v94.53C39.062 51.595 22.396 46.126 0 46.126s-39.063 5.47-50 16.406V-32c0-19.27 16.667-28.906 50-28.906z"
      },
      {
        type: "negative",
        data:
          "M.08-42.56c9.585.206 20.126.53 27.954 6.822 4.96 3.9 4.71 10.792 4.574 16.482v51.278C22.09 27.066 7.283 26.072.168 26.01c-7.72.23-21.895.935-32.616 4.674.04-19.197-.083-38.395.064-57.59.567-7.5 7.834-12.33 14.62-13.774 5.818-1.498 11.857-1.86 17.844-1.88z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 123.438
    },
    origin: {
      x: 50,
      y: 60.906
    },
    align: "left"
  },
  PunctumQuadratum: {
    paths: [
      {
        type: "positive",
        data:
          "M0-60.906c33.333 0 50 9.635 50 28.906v94.53C39.062 51.595 22.396 46.126 0 46.126s-39.063 5.47-50 16.406V-32c0-19.27 16.667-28.906 50-28.906z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 123.438
    },
    origin: {
      x: 50,
      y: 60.906
    },
    align: "left"
  },
  PunctumQuadratumLiquescent: {
    paths: [
      {
        type: "positive",
        data:
          "M0-36.5436c19.999799999999997 0 30 5.781 30 17.3436v56.717999999999996C23.437199999999997 30.956999999999997 13.4376 27.6756 0 27.6756s-23.4378 3.2819999999999996-30 9.843599999999999V-19.2c0-11.562 10.000200000000001-17.3436 30-17.3436z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 123.438
    },
    origin: {
      x: 50,
      y: 60.906
    },
    align: "left"
  },
  PunctumQuadratumAscLiquescent: {
    paths: [
      {
        type: "positive",
        data:
          "M-50 43.688V-61c4.167 7.292 12.76 10.938 25.78 10.938 9.376 0 20.053-1.563 32.032-4.688C31.773-60.48 45.833-71.677 50-88.344v117.97C43.75 42.645 32.812 51.5 17.187 56.186-.52 61.398-15.886 64-28.906 64-42.97 64-50 57.23-50 43.687z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 152.344
    },
    origin: {
      x: 50,
      y: 88.344
    },
    align: "left"
  },
  PunctumQuadratumDesLiquescent: {
    paths: [
      {
        type: "positive",
        data:
          "M-50-56.03c0-13.022 7.03-19.532 21.094-19.532 13.02 0 28.385 2.604 46.093 7.812C32.813-63.583 43.75-54.73 50-41.187V76C45.833 59.854 31.77 48.656 7.812 42.406c-11.98-3.125-22.656-4.687-32.03-4.687-13.022 0-21.615 3.905-25.782 11.718v-105.47z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 151.562
    },
    origin: {
      x: 50,
      y: 75.562
    },
    align: "left"
  },
  PunctumInclinatum: {
    paths: [
      {
        type: "positive",
        data: "M0-75.78L50 0 0 75-50 0 0-75.78z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 150.78
    },
    origin: {
      x: 50,
      y: 75.78
    },
    align: "left"
  },
  // PunctumInclinatum leaning to the right, as in an ascending line (turned 6° clockwise)
  PunctumInclinatumAscendens: {
    paths: [
      {
        type: "positive",
        data: "M7.921-75.365L49.726 5.226-7.84 74.589-49.726-5.226z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 150.78
    },
    origin: {
      x: 50,
      y: 75.78
    },
    align: "left"
  },
  // PunctumInclinatum leaning to the left, as in a descending line (turned 6° anticlockwise)
  PunctumInclinatumDescendens: {
    paths: [
      {
        type: "positive",
        data: "M-7.921-75.365L49.726-5.226 7.84 74.589-49.726 5.226z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 150.78
    },
    origin: {
      x: 50,
      y: 75.78
    },
    align: "left"
  },
  // the top half of PunctumInclinatum, and the tail of Stropha below it, a
  // quarter longer
  PunctumInclinatumAuctus: {
    paths: [
      {
        type: "positive",
        data:
          "M0-75.78L50 0 34.812 34.18C18.666 69.338-.084 90.495-21.438 97.655c4.687-4.556 7.03-10.741 7.03-18.554 0-11.066-4.947-25.715-14.843-43.944L-50 0z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 173.435
    },
    origin: {
      x: 50,
      y: 75.78
    },
    align: "left"
  },
  PunctumInclinatumLiquescent: {
    paths: [
      {
        type: "positive",
        data: "M 0,-53.164 35,-0.117 0,52.383 -35,-0.117 0,-53.164 z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 105.546
    },
    origin: {
      x: 50,
      y: 53.164
    },
    align: "left"
  },
  Quilisma: {
    paths: [
      {
        type: "positive",
        data:
          "M-50 34.938V-51c5.73 20.833 13.02 31.25 21.875 31.25 7.813 0 12.5-15.625 14.063-46.875 3.645 12.5 6.9 21.224 9.765 26.172s6.9 7.422 12.11 7.422c5.208 0 9.374-14.324 12.5-42.97 5.73 22.917 10.677 34.375 14.843 34.375 5.73 0 10.677-15.885 14.844-47.656v100c0 17.707-3.125 26.56-9.375 26.56-4.688 0-9.115-5.988-13.28-17.968-2.085 21.875-8.074 32.813-17.97 32.813-7.813 0-16.146-7.292-25-21.875-4.688 20.312-10.677 30.47-17.97 30.47-5.207 0-9.244-2.605-12.108-7.814C-48.568 47.698-50 41.708-50 34.938z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 150
    },
    origin: {
      x: 50,
      y: 89.282
    },
    align: "left"
  },
  TerminatingAscLiquescent: {
    paths: [
      {
        type: "positive",
        data:
          "M-9.375 40.22c0-11.98-4.948-17.97-14.844-17.97-10.936 0-19.53 3.646-25.78 10.938v-53.126c0-6.77 2.604-12.76 7.813-17.968 5.208-5.21 10.677-8.594 16.406-10.157 2.603-.52 5.207-.78 7.81-.78 3.647 0 7.032.78 10.157 2.343C-2.603-43.896 0-39.73 0-34V73.03h-9.375V40.22z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 49.999,
      height: 121.873
    },
    origin: {
      x: 49.999,
      y: 48.843
    },
    align: "right"
  },
  TerminatingDesLiquescent: {
    paths: [
      {
        type: "positive",
        data:
          "M-9.375-48.156V-80.97H0V26.845c0 5.73-2.604 9.896-7.813 12.5-3.125 1.562-6.51 2.343-10.156 2.343-2.603 0-5.207-.26-7.81-.78-5.73-1.563-11.2-4.95-16.407-10.157C-47.398 25.542-50 19.292-50 12v-52.344c6.25 7.292 14.844 10.938 25.78 10.938 9.897 0 14.845-6.25 14.845-18.75z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 50,
      height: 122.658
    },
    origin: {
      x: 50,
      y: 80.97
    },
    align: "right"
  },
  VerticalEpisemaAbove: {
    paths: [
      {
        type: "positive",
        data: "M-8-4c2 3 6 4 8 4s6-1 8-4v-52c-2-3-6-4-8-4s-6 1-8 4z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 16,
      height: 60
    },
    origin: {
      x: 8,
      y: 60
    },
    align: "left"
  },
  VerticalEpisemaBelow: {
    paths: [
      {
        type: "positive",
        data: "M-8 56c2 3 6 4 8 4s6-1 8-4v-52c-2-3-6-4-8-4s-6 1-8 4z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 16,
      height: 60
    },
    origin: {
      x: 8,
      y: 0
    },
    align: "left"
  },
  VirgaLong: {
    paths: [
      {
        type: "positive",
        data:
          "M50-38v285.156c0 6.77-2.344 10.937-7.03 12.5-1.564 0-2.605-.78-3.126-2.344-.52-1.562-.782-10.156-.782-25.78V54.186C29.168 45.334 16.146 40.907 0 40.907c-22.917 0-39.583 5.208-50 15.624V-38c0-19.27 16.667-28.906 50-28.906S50-57.27 50-38z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 326.562
    },
    origin: {
      x: 50,
      y: 66.906
    },
    align: "left"
  },
  VirgaShort: {
    paths: [
      {
        type: "positive",
        data:
          "M50-38v211.72c0 7.29-2.344 11.457-7.03 12.5-1.564 0-2.606-.783-3.126-2.345-.52-1.563-.782-10.156-.782-25.78V54.187C29.167 45.332 16.146 40.906 0 40.906c-22.917 0-39.583 5.21-50 15.625V-38c0-19.27 16.667-28.906 50-28.906S50-57.27 50-38z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 100,
      height: 253.126
    },
    origin: {
      x: 50,
      y: 66.906
    },
    align: "left"
  },
  Virgula: {
    paths: [
      {
        type: "positive",
        data:
          "M8.178-55.66c0-22.137 12.092-33.2 36.287-33.2 11.835 0 23.53 5.66 35.108 16.98C91.15-60.547 96.94-41.766 96.94-15.534c0 53.515-31.646 87.487-94.937 101.895-2.048-2.06-3.077-5.146-3.077-9.273 0-1.03.247-1.8.76-2.316 42.71-19.027 64.075-41.678 64.075-67.92 0-11.322-2.325-20.326-6.945-27.016-4.62-6.69-9.52-11.052-14.676-13.11-5.147-2.048-11.836-3.85-20.07-5.403C12.81-39.707 8.18-45.37 8.18-55.66z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 98.014,
      height: 175.221
    },
    origin: {
      x: 1.074 - 98.014 / 2, // centered
      y: 88.86
    },
    align: "left"
  },
  // parentheses around a sign (Gregorio's parenthesized accidentals and
  // bars), as thick in the middle as the stems of Flat and Natural
  OpenParenthesis: {
    paths: [
      {
        type: "positive",
        data: "M40-150C16-118 0-62 0 0s16 118 40 150c3 .5 6.5-1.5 7-4C29 112 17 60 17 0s12-112 30-146c-.5-2.5-4-4.5-7-4z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 47,
      height: 300
    },
    origin: {
      x: 0,
      y: 150
    },
    align: "left"
  },
  CloseParenthesis: {
    paths: [
      {
        type: "positive",
        data: "M7-150c24 32 40 88 40 150S31 118 7 150c-3 .5-6.5-1.5-7-4C18 112 30 60 30 0S18-112 0-146c.5-2.5 4-4.5 7-4z"
      }
    ],
    bounds: {
      x: 0,
      y: 0,
      width: 47,
      height: 300
    },
    origin: {
      x: 0,
      y: 150
    },
    align: "left"
  }
};

// The hollow (cavum) form of each note glyph, for a note with a cavum (gabc
// r): the glyph, with a hole drawn in ChantContext.negativeFillColor, as
// PunctumCavum is PunctumQuadratum with one.  Each hole is its glyph's
// outline inset by the thickness of PunctumCavum's walls (18), or by as much
// less as the glyph is smaller than a punctum (11 to 12.6 for the small
// liquescents) and by 13 for the quilisma, whose strokes are thinner.  (A
// porrectus's swash makes its own hole, as it does its outline: see
// porrectusSwash().)
const cavumHoles: { [glyph: string]: string } = {
  OriscusAsc:
    "M-29.685-46.805c-1.903 3.006-2.3 7.028-2.293 10.568l.123 58.886c0 .165.33-.013.495-.026 .831-.06 1.66-.134 2.493-.171 2.164-.094 4.335-.058 6.492.147 6.038.572 11.671 2.644 17.098 5.274l11.246 5.451c6.039 2.926 17.344 10.645 23.747 8.96 1.631-3.078 2.245-6.662 2.251-10.142l.033-19.996-.178-39.918c-.003-.663-1.323.101-1.984.142-2.329.146-4.668.145-6.991-.092-6.062-.618-12.187-2.57-17.639-5.277l-11.641-5.781c-3.274-1.625-15.867-7.527-18.776-8.167-1.453-.32-3.029-.053-4.476.142z",
  OriscusDes:
    "M-32-27.166l.02 59.487c.001 3.645.562 7.412 2.311 10.626 7.896 1.678 28.816-11.865 37.795-15.492 4.918-1.987 10.053-3.747 15.357-4.292 2.156-.222 4.329-.195 6.49-.053 .154.01 1.984.174 1.984.099l.018-58.956c.001-3.474-.507-7.07-2.007-10.209-5.418-1.659-12.486 2.494-17.334 4.89l-11.203 5.537c-6.923 3.422-14.388 6.624-22.018 8.016-2.471.45-4.953.624-7.463.555-1.001-.028-1.995-.112-2.994-.171-.318-.019-.956-.356-.956-.037z",
  OriscusLiquescent:
    "M-32-27.683l.029 59.519c.002 3.155.515 6.387 1.913 9.214 5.083 2.162 11.744-2.077 16.253-4.501l10.572-5.684c7.231-3.889 15.201-7.35 23.333-8.734 2.607-.443 5.329-.719 7.968-.443 1.007.105 1.957.375 2.945.559 .301.056.91.415.91.109l.072-57.995c.005-3.475-.469-7.086-2.059-10.188-7.444-3.127-29.269 11.115-37.544 14.29-5.122 1.966-10.484 3.508-15.977 3.899-2.161.154-4.337.113-6.496-.055-.176-.014-1.919-.286-1.919.01z",
  PodatusLower:
    "M31.8-37.362c0-.167-.316.105-.475.156-2.066.655-4.164 1.191-6.292 1.6-4.796.921-9.562 1.033-14.42 1.206l-15.483.551c-5.981.212-12.012-.078-17.975-.554-2.158-.172-4.314-.379-6.467-.619-.827-.092-2.48-1.123-2.482-.29l-.157 67.247c0 .226 1.753.373 1.941.395 4.944.593 9.993.735 14.968.785l16.491.163c7.883.078 15.576.935 23.445-.152 1.276-.177 6.863-1.19 6.863-1.342z",
  PodatusLowerShort:
    "M-31.862-12.448l-.024 55.314c0 .087 1.827.081 1.985.083 3.831.064 7.663.101 11.495.127 12.827.086 25.656.075 38.484-.031 3.498-.029 6.996-.067 10.495-.127 .122-.002 1.427.066 1.427-.168v-55.477c0-.289-1.28-.091-1.4-.083-2.992.198-5.993.268-8.99.331-7.161.152-14.326.181-21.49.195-7.163.013-14.327.002-21.491-.054-2.332-.018-4.664-.042-6.996-.075-.29-.004-3.495-.062-3.495-.035z",
  PodatusUpper:
    "M-73.296 22.241c0 .331.649-.122.973-.191 .652-.139 1.3-.298 1.952-.438 2.119-.453 4.254-.837 6.397-1.156 6.431-.958 12.947-1.344 19.446-1.261 5.994.076 12.015.583 17.873 1.9 1.951.439 3.883.969 5.78 1.608 .631.213 1.25.467 1.883.675 .309.102.938.598.938.272l.036-50.478c.004-5.577-6.83-7.714-11.254-8.955-10.512-2.95-23.131-2.914-33.723-.349-4.764 1.154-10.39 3.15-10.38 8.944z",
  PodatusUpperShort:
    "M-73.296 14.244c.001.331.65-.122.973-.191 .652-.139 1.3-.299 1.953-.438 2.119-.453 4.253-.837 6.397-1.156 6.43-.958 12.947-1.344 19.445-1.261 5.995.076 12.016.583 17.874 1.901 1.952.439 3.884.968 5.78 1.607 .631.213 1.25.467 1.883.676 .309.102.938.597.938.271l.035-50.48c.004-5.576-6.83-7.715-11.253-8.956-10.513-2.95-23.132-2.911-33.724-.346-4.765 1.154-10.39 3.15-10.38 8.944z",
  PunctumQuadratumLiquescent:
    "M-18.627-21.23l-.07 40.216c-.001.5.966-.261 1.451-.381 1.618-.402 3.253-.732 4.898-.998 4.114-.666 8.287-.936 12.452-.931 4.166.005 8.338.285 12.45.964 1.48.245 2.951.541 4.409.895 .144.035 1.917.465 1.917.417l-.36-40.36c-.013-1.395-6.506-3.069-7.672-3.287-7.303-1.37-15.619-1.395-22.88.243-1.223.276-6.592 1.447-6.595 3.222z",
  PunctumQuadratumAscLiquescent:
    "M-31.837-32.405l.225 78.223c0 .234 4.51.163 4.991.15 5.666-.155 11.311-.842 16.89-1.822 11.125-1.954 24.166-4.158 33.621-10.69 1.507-1.042 7.966-6.423 7.97-8.187l.14-68.973c0-.224-2.884.992-3.156 1.104-2.158.889-4.348 1.698-6.562 2.436-6.311 2.105-12.836 3.691-19.347 5.047-7.666 1.596-15.454 2.699-23.285 2.984-2.499.09-5.002.103-7.5-.007-.999-.044-1.996-.116-2.994-.192-.331-.025-.994-.405-.993-.073z",
  PunctumQuadratumDesLiquescent:
    "M-31.8-57.367l-.129 77.425c-.001.331.661-.025.991-.046 .998-.065 1.994-.158 2.994-.206 2.498-.119 5.002-.108 7.501-.019 7.834.278 15.626 1.377 23.295 2.969 6.688 1.389 13.336 3.089 19.782 5.361 2.045.72 4.069 1.499 6.066 2.342 .766.323 1.517.7 2.292 1.002 .29.113.887.605.888.293l.12-68.482c.003-1.962-6.113-7.271-7.719-8.4-9.253-6.502-22.76-8.58-33.698-10.524-5.578-.992-11.223-1.694-16.89-1.869-1.667-.051-3.337-.087-4.997.086-.166.017-.496-.099-.496.068z",
  PunctumInclinatum:
    "M0-43.096l-28.33 42.991 28.191 42.447 28.469-42.447z",
  PunctumInclinatumAscendens:
    "M4.474-42.823l-32.681 39.866 23.782 45.126 32.606-39.237z",
  PunctumInclinatumDescendens:
    "M-4.526-42.819l-23.674 45.682 32.479 39.248 23.88-45.192z",
  PunctumInclinatumAuctus:
    "M0-43.096l-28.667 43.448 15.294 26.326c3.914 6.737 7.207 14.007 10.061 21.249 1.272 3.227 2.149 7.192 3.773 10.028 6.696-9.086 12.497-19.05 17.1-29.362l8.363-18.739c.909-2.036 3.614-8.183 3.57-8.249z",
  PunctumInclinatumLiquescent:
    "M0-30.285l-19.807 30.021 19.807 29.932 19.807-29.932z",
  Quilisma:
    "M24.6-32.622c-4.905 10.122-13.897 14.85-24.925 11.367-2.476-.782-4.833-2.35-7.072-3.16-2.865 7.467-7.527 14.66-15.66 16.963-3.061.867-6.299.879-9.415.306-.984-.181-1.956-.424-2.91-.726-.477-.15-1.414-.996-1.416-.496l-.185 44.383c-.014 3.456.854 6.964 2.413 10.023 4.956-9.487 4.655-27.984 18.194-28.768 3.348-.194 6.701.944 9.241 3.134 6.161 5.311 8.138 15.17 16.235 18.504 5.555-8.74 2.645-23.683 10.055-29.693 3.408-2.764 8.097-3.625 12.264-2.25 1.267.418 2.468 1.033 3.549 1.815 .407.295.782.619 1.162.948 .244.211.725.963.726.641l.144-38.913c.001-.289-.57-.1-.859-.113-1.352-.059-2.637.002-3.981-.265-2.867-.569-5.25-1.972-7.56-3.7z",
  Stropha:
    "M-1.2-37.496l-25.046 37.369 14.762 22.503c4.616 7.036 7.991 14.516 11.495 22.108 7.228-7.453 13.756-16.086 18.693-25.241l9.468-17.558-20.798-26.883c-2.036-2.632-3.941-5.382-5.806-8.137-.654-.966-1.299-1.938-1.935-2.916-.272-.419-.555-1.661-.833-1.245z",
  StrophaLiquescent:
    "M-1.2-37.496l-25.781 38.656 16.212 30.45c4.451 8.361 8.154 17.222 10.924 26.277 5.54-4.597 14.722-22.96 17.829-30.2l10.818-25.212-21.426-27.671c-2.038-2.632-3.942-5.383-5.807-8.138-.654-.966-1.3-1.938-1.935-2.917-.273-.418-.557-1.661-.834-1.245z",
  TerminatingAscLiquescent:
    "M-38.999 13.115c0 .205 4.553-.915 5.281-1.046 3.278-.592 6.63-.863 9.961-.816 2.835.041 5.672.426 8.379 1.284 .954.302 1.89.662 2.8 1.08 .454.209 1.343 1.171 1.343.671l-.035-49.846c-.001-2.464-9.411-2.194-10.921-1.969-7.006 1.048-16.758 9.02-16.776 16.613l-.032 14.011z",
  TerminatingDesLiquescent:
    "M-38.76-20.427l-.217 33.352c-.052 7.876 8.727 15.732 16.016 17.321 1.542.336 11.511.762 11.531-1.623l.43-49.981c0-.005-3.66 1.39-4.068 1.529-2.852.968-5.857 1.391-8.862 1.421-3.333.035-6.685-.244-9.964-.849-1.149-.212-2.29-.465-3.42-.763-.484-.128-1.442-.908-1.446-.407z"
};
for (const [glyph, hole] of Object.entries(cavumHoles)) {
  Glyphs[glyph + "Cavum"] = {
    ...Glyphs[glyph],
    paths: [...Glyphs[glyph].paths, { type: "negative", data: hole }]
  };
}

// -----------------------------------------------------------------------------
// the swash of a porrectus

type Point = [number, number];
type Curve = [Point, Point, Point, Point];

// A porrectus's swash, the stroke from its first note down to its second, is
// made for the fall between them and the width it's given, rather than
// stored, so that a porrectus can fall by any amount (with the Chi-Rho
// clef's offsets, a third of a staff position as well) and keep the size of
// its notes under a glyph multiplier.  Its left edge is the first note's; its
// top falls to a trough over the second note and turns up into the top right
// corner; its right edge comes down to a rounded corner under the second
// note, its lowest point; and its bottom rises from there to the left edge.
//
// Each row is the swash's shape for a fall of 1 to 5 staff positions, fitted
// to the outlines that exsurge drew those with (Porrectus1 to Porrectus5, to
// within about a glyph unit of each).  A fall between two rows takes the
// shape between them, and one below 1 or above 5 the shape of the nearest.
// A row has: the top of the left edge (aT); the trough, in from the right
// edge (tX) and from the second note (tY); the top (cY) and bottom (rY) of
// the right edge, from the second note; the lowest point, in from the right
// edge (eX) and from the second note (eY); and for the top and the bottom,
// the angle (d0, d1), in degrees, that each leaves the left edge at, further
// down than the straight line to the trough or the lowest point, and how far
// each one's handles go toward where its ends' tangents meet (l0 and l1 for
// the top, from the left edge and the trough; l2 and l3 for the bottom, from
// the lowest point and the left edge).
const swashShapes = [
  // aT, tX, tY, cY, rY, eX, eY, d0, l0, l1, d1, l2, l3
  [-52.75, 39.8, -34, -63.7, 51.9, 18.7, 62.9, 16, 1.08, 0.22, 21.5, 0.4, 0.96],
  [-68.75, 32, -37.5, -67.2, 47.7, 18, 59.4, 19.1, 0.67, 0.35, 21.4, 0.69, 0.17],
  [-70, 26.5, -38, -70.8, 43.3, 10.2, 57.3, 12.8, 0.62, 0.28, 11.6, 0.11, 0.83],
  [-70, 27.8, -38, -73.1, 41.7, 9.6, 55.8, 9.9, 0.52, 0.3, 8.3, 0.05, 0.95],
  [-70, 30.7, -46.1, -74.7, 40.2, 10.6, 52, 8.2, 0.52, 0.26, 6.8, 0.02, 0.92]
];
// the widths of the swashes for falls of 1 to 5
const swashWidths = [302.344, 377.343, 377.344, 420, 462.656];

/**
 * The width of a porrectus's swash for a fall of interval staff positions,
 * in glyph units: between the widths for the whole numbers either side of it,
 * and below 1 and above 5, narrower or wider by as much again for each staff
 * position as from 1 to 2 and from 4 to 5.
 */
export function porrectusSwashWidth(interval: number) {
  const i = Math.min(3, Math.max(0, Math.floor(interval) - 1));
  return swashWidths[i] + (interval - 1 - i) * (swashWidths[i + 1] - swashWidths[i]);
}

const lerp = (p: Point, q: Point, k: number): Point => [p[0] + k * (q[0] - p[0]), p[1] + k * (q[1] - p[1])];

// a curve from p to q, where it's level, leaving p at angle degrees further
// down than the straight line to q, with its handles going kp and kq of the
// way toward where those two tangents meet
function swashEdge(p: Point, q: Point, angle: number, kp: number, kq: number): Curve {
  const a = Math.atan2(q[1] - p[1], q[0] - p[0]) + (angle * Math.PI) / 180;
  const meet: Point = [p[0] + (q[1] - p[1]) / Math.tan(a), q[1]];
  return [p, lerp(p, meet, kp), lerp(q, meet, kq), q];
}

// the point t of the way along curve c
function pointOn(c: Curve, t: number): Point {
  const u = 1 - t;
  return [0, 1].map(
    (i) => u * u * u * c[0][i] + 3 * u * u * t * c[1][i] + 3 * u * t * t * c[2][i] + t * t * t * c[3][i]
  ) as Point;
}

// curve c split at t
function splitCurve(c: Curve, t: number): [Curve, Curve] {
  const p01 = lerp(c[0], c[1], t), p12 = lerp(c[1], c[2], t), p23 = lerp(c[2], c[3], t),
    p012 = lerp(p01, p12, t), p123 = lerp(p12, p23, t), mid = lerp(p012, p123, t);
  return [[c[0], p01, p012, mid], [mid, p123, p23, c[3]]];
}

// the unit normal to the right of the direction from p to q: toward the
// inside of an outline drawn clockwise, as the swash's is
function inward(p: Point, q: Point): Point {
  const dx = q[0] - p[0], dy = q[1] - p[1], length = Math.hypot(dx, dy);
  return [-dy / length, dx / length];
}

// curve c moved d toward the inside of the outline, in as many pieces as
// given: each piece's ends move along their normals, its handles keep their
// directions, and they're as long as makes its middle move d too
function insetCurve(c: Curve, d: number, pieces: number): Curve[] {
  const insetPieces: Curve[] = [];
  for (let i = pieces; i > 0; i--) {
    const [piece, rest] = splitCurve(c, 1 / i);
    c = rest;
    const n0 = inward(piece[0], piece[1]), n1 = inward(piece[2], piece[3]), half = splitCurve(piece, 0.5)[0],
      nMid = inward(half[2], half[3]);
    const p0: Point = [piece[0][0] + d * n0[0], piece[0][1] + d * n0[1]],
      p3: Point = [piece[3][0] + d * n1[0], piece[3][1] + d * n1[1]];
    // with the handles' directions u0 and u1, the middle (p0 + p3) / 2 +
    // 3/8 (a u0 - b u1) is where it should be for handles a and b long
    const u0: Point = [n0[1], -n0[0]], u1: Point = [n1[1], -n1[0]],
      rx = ((half[3][0] + d * nMid[0] - (p0[0] + p3[0]) / 2) * 8) / 3,
      ry = ((half[3][1] + d * nMid[1] - (p0[1] + p3[1]) / 2) * 8) / 3,
      det = u1[0] * u0[1] - u0[0] * u1[1];
    let a = (u1[0] * ry - rx * u1[1]) / det,
      b = (u0[0] * ry - u0[1] * rx) / det;
    // (where the piece is all but straight, that says little, and its handles stay as long as they were)
    if (!(Math.abs(det) > 1e-3 && a > 0 && b > 0)) {
      a = Math.hypot(piece[1][0] - piece[0][0], piece[1][1] - piece[0][1]);
      b = Math.hypot(piece[3][0] - piece[2][0], piece[3][1] - piece[2][1]);
    }
    insetPieces.push([p0, [p0[0] + a * u0[0], p0[1] + a * u0[1]], [p3[0] - b * u1[0], p3[1] - b * u1[1]], p3]);
  }
  return insetPieces;
}

// where curves (in turn, or from the last back if fromEnd is set) first cross
// x: the index of the curve that does and the curve up to there, or from
// there on if after is set
function crossing(curves: Curve[], x: number, after: boolean, fromEnd = false): [number, Curve] {
  for (let k = 0; k < curves.length; k++) {
    const i = fromEnd ? curves.length - 1 - k : k, c = curves[i], f = (t: number) => pointOn(c, t)[0] - x;
    for (let s = 0; s < 16; s++) {
      let lo = fromEnd ? 1 - s / 16 : s / 16,
        hi = fromEnd ? lo - 1 / 16 : lo + 1 / 16;
      if (f(lo) * f(hi) > 0) continue;
      for (let j = 0; j < 30; j++) {
        const mid = (lo + hi) / 2;
        if (f(lo) * f(mid) <= 0) hi = mid;
        else lo = mid;
      }
      return [i, splitCurve(c, (lo + hi) / 2)[after ? 1 : 0]];
    }
  }
  throw new Error("porrectusSwash: the hole's edges don't meet");
}

const pathPoint = (p: Point) => Math.round(p[0] * 1000) / 1000 + " " + Math.round(p[1] * 1000) / 1000;

// svg path data for curves (a line being [p, p, q, q]), joined by lines
// where one doesn't start where the last ended
function pathData(curves: Curve[]) {
  let last = pathPoint(curves[0][0]),
    data = "M" + last;
  for (const c of curves) {
    const [start, end] = [pathPoint(c[0]), pathPoint(c[3])];
    if (start !== last) data += "L" + start;
    if (c[1] !== c[0] || c[2] !== c[3]) data += "C" + c.slice(1).map(pathPoint).join(" ");
    else if (end !== start) data += "L" + end;
    last = end;
  }
  return data + "z";
}

/**
 * A porrectus's swash (see above), falling drop glyph units (100 to a staff
 * position) over width; hollow, if it is, with the hole that a punctum cavum
 * has, its outline inset by 18.  Its origin is in the middle of its first note.
 */
export function porrectusSwash(drop: number, width: number, hollow = false): Glyph {
  const at = Math.min(4, Math.max(0, drop / 100 - 1)),
    row = Math.min(3, Math.floor(at));
  const [aT, tX, tY, cY, rY, eX, eY, d0, l0, l1, d1, l2, l3] = swashShapes[row].map(
    (value, i) => value + (at - row) * (swashShapes[row + 1][i] - value)
  );
  const x1 = width - 50,
    A: Point = [-50, aT],
    B: Point = [-50, aT + 118.75],
    T: Point = [x1 - tX, drop + tY],
    C: Point = [x1, drop + cY],
    R: Point = [x1, drop + rY],
    E: Point = [x1 - eX, drop + eY];
  const top = swashEdge(A, T, d0, l0, l1),
    upturn: Curve = [T, [T[0] + 8.5, T[1]], [x1 - 8.1, C[1] + 29.2], C],
    bottom = swashEdge(B, E, d1, l3, l2).reverse() as Curve;
  const paths: GlyphPath[] = [
    { type: "positive", data: pathData([top, upturn, [C, C, R, R], [R, [x1, R[1] + 10.8], [E[0] + 5.9, E[1]], E], bottom]) }
  ];

  if (hollow) {
    // where the edges moved in cross, their corners are; and at the bottom
    // right, the rounded corner being tighter than the walls are thick, the
    // right edge meets the bottom (carried on level, if it would start
    // further left)
    const d = 18,
      tops = insetCurve(top, d, 4),
      upturns = insetCurve(upturn, d, 2),
      bottoms = insetCurve(bottom, d, 4),
      [ta, topFrom] = crossing(tops, -50 + d, true),
      [tc, upturnTo] = crossing(upturns, x1 - d, false),
      level = bottoms[0][0][0] < x1 - d,
      [te, bottomFrom] = level ? [0, bottoms[0]] : crossing(bottoms, x1 - d, true, true),
      [tb, bottomTo] = crossing(bottoms, -50 + d, false);
    const bottomPart = te === tb ? [crossing([bottomFrom], -50 + d, false)[1]] : [bottomFrom, ...bottoms.slice(te + 1, tb), bottomTo];
    const corner: Point = [x1 - d, bottoms[0][0][1]];
    paths.push({
      type: "negative",
      data: pathData([
        topFrom,
        ...tops.slice(ta + 1),
        ...upturns.slice(0, tc),
        upturnTo,
        ...(level ? [[corner, corner, corner, corner] as Curve] : []),
        ...bottomPart
      ])
    });
  }

  const above = Math.max(-aT, -C[1]);
  return {
    paths,
    bounds: { x: 0, y: 0, width, height: above + E[1] },
    origin: { x: 50, y: above },
    align: "left"
  };
}

// the swashes for falls of 1 to 5, which exsurge stored the outlines of
for (let interval = 1; interval <= 5; interval++) {
  const drop = 100 * interval, width = porrectusSwashWidth(interval);
  Glyphs["Porrectus" + interval] = porrectusSwash(drop, width);
  Glyphs["Porrectus" + interval + "Cavum"] = porrectusSwash(drop, width, true);
}
